/*
 *  Copyright (c) Qwoach LLC 2022. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 8/9/2022
 */

import {Type, plainToClass, classToPlain} from "@qwoach/class-transformer-wrapper";
import {Transform} from "class-transformer"

import CoachingPackageTemplate from "@shared/CoachingPackage/CoachingPackageTemplate";
import InvoicesInfo from "@shared/Invoice/InvoicesInfo";
import {applyClientName, applyCoachName} from "@/core/components/template/PlaceholdersData";
import CoachingPackageRef from "@shared/CoachingPackage/CoachingPackageRef";
import {CoachingPackageType} from "@shared/CoachingPackage/CoachingPackageType";
import ParticipantItem from "@shared/scheduler/ParticipantItem";
import {StatusEnum} from "@/core/components/Status/StatusEnum";
import {timeNow} from "@/utils/helpers";
import {ObjectTypeEnum} from "@shared/Invoice/ObjectTypeEnum.js";
import QwoachFileRef from "@shared/Drive/QwoachFileRef.js";

let transform = ({value}) => {
	console.log("ParticipantWelcomeFilesMap.transform value = ", value)
	Object.keys(value).forEach(key => {
		value[key] = plainToClass(QwoachFileRef, value[key])
	})
	return value;
}

export default class PurchasedCoachingPackage extends CoachingPackageTemplate {

	clientId: string = null
	clientName: string = null

	parentTemplateId: string = null

	@Type(() => InvoicesInfo)
	invoicesInfo: InvoicesInfo = new InvoicesInfo()

	@Type(() => InvoicesInfo)
	invoicesInfoGroup: InvoicesInfo[] = []

	status: StatusEnum = StatusEnum.ACTIVE

	@Type(() => ParticipantItem)
	participantsList: ParticipantItem[] = []

	@Transform(transform)
	participantFileRefsMap = {}

	sharedWithIds: string[] = []

	completionMap = {}


	constructor(packageType) {
		super(packageType);
	}

	getCurrentSchemaVersion() {
		return 6
	}


	getParticipantsAsClientProfiles(){
		return this.participantsList.map(item => item.asClientProfile())
	}
	getJoinedParticipantsAsClientProfiles(){
		return this.participantsList
			.filter(participant => participant.isInviteAccepted())
			.map(item => item.asClientProfile())
	}

	setClientProfile(clientProfile){
		if(this.isOneOnOnePackage()){
			this.clientId = clientProfile.id
			this.clientName = clientProfile.name
		}
		this.addParticipant(clientProfile.asParticipant())
	}

	setInvoiceInfo(invoiceItem){
		let invoiceInfo = this.getInvoiceInfo(invoiceItem.billToClientId)
		invoiceInfo.setInvoice(invoiceItem)
	}

	getInvoiceInfo(clientId){
		if(this.isOneOnOnePackage()){
			return this.invoicesInfo
		} else if(clientId){
			return this.getOrAddGroupInvoiceInfo(clientId)
		} else {
			return this.getCombinedGroupInvoiceInfo()
		}
	}

	getValidTillMs(clientId){
		let invoiceInfo = this.getInvoiceInfo(clientId)
		return invoiceInfo.validTillMs
	}

	getOrAddGroupInvoiceInfo(clientId){
		let invoiceInfo
		let invoiceIndex = this.invoicesInfoGroup.findIndex(info => info.clientId === clientId)
		if(invoiceIndex < 0){
			invoiceInfo = new InvoicesInfo()
			invoiceInfo.clientId = clientId
			this.invoicesInfoGroup.push(invoiceInfo)
		} else {
			invoiceInfo = this.invoicesInfoGroup[invoiceIndex]
		}
		return invoiceInfo
	}

	getCombinedGroupInvoiceInfo(){
		let invoiceInfoCombined = new InvoicesInfo()
		let allInvoiceInfoList = []
		for(let participant of this.participantsList){
			let invoiceInfo = this.getInvoiceInfo(participant.id)
			allInvoiceInfoList.push(invoiceInfo)
		}
		for(let i = this.participantsList.length; i < this.groupSettings.participantsLimit; ++i){
			let invoiceInfo = new InvoicesInfo()
			invoiceInfo.amountTotal = this.totalPrice()
			allInvoiceInfoList.push(invoiceInfo)
		}
		for(let invoiceInfo of allInvoiceInfoList){
			invoiceInfoCombined.amountTotal += invoiceInfo.amountTotal
			invoiceInfoCombined.amountPaid += invoiceInfo.amountPaid
		}
		return invoiceInfoCombined
	}

	setInstallmentPaid(invoiceItem){
		this.getInvoiceInfo(invoiceItem.billToClientId).setInstallmentPaid(invoiceItem)
	}

	addParticipant(participant){
		let index = this.sharedWithIds.findIndex(id => id === participant.id)
		if(index < 0){
			this.participantsList.push(participant)
			this.sharedWithIds.push(participant.id)
		} else {
			this.participantsList.splice(index, 1, participant)
		}
	}

	removeParticipant(participant){
		let index = this.participantsList.findIndex(p => p.id === participant.id)
		if(index >= 0){
			this.participantsList.splice(index, 1)
			let index2 = this.sharedWithIds.findIndex(id => id === participant.id)
			if(index2 >= 0){
				this.sharedWithIds.splice(index2, 1)
			}
		}
	}


	findParticipantById(clientId){
		let index = this.participantsList.findIndex(participant => participant.id === clientId)
		return index >= 0 ? this.participantsList[index] : null
	}
	// hasParticipant(clientId){
	// 	let index = this.sharedWithIds.findIndex(id => id === clientId)
	// 	return index >= 0
	// }
	hasActiveParticipant(clientId){
		let index = this.sharedWithIds.findIndex(id => id === clientId)
		return index >= 0 && this.participantsList[index].isInviteAccepted()
	}

	getAmountPaid(clientId){
		return Math.max(0, this.getInvoiceInfo(clientId).amountPaid)
	}
	getAmountTotal(clientId){
		return Math.max(0, this.getInvoiceInfo(clientId).amountTotal)
	}
	getInstallmentsPaid(clientId){
		return this.getInvoiceInfo(clientId).installmentsPaid
	}
	getInstallmentsTotal(clientId){
		return this.getInvoiceInfo(clientId).installmentsTotal
	}
	getInstallmentsPaidFormatted(clientId){
		let invoiceInfo = this.getInvoiceInfo(clientId)
		if(invoiceInfo.installmentsTotal > 1){
			return `paid ${invoiceInfo.installmentsPaid} out of ${invoiceInfo.installmentsTotal}`
		}
		return ""
	}

	// syncValidTillFromInvoice(invoiceItem){
	// 	this.validTillMs = invoiceItem.validTillMs || -1
	// }

	createWelcomeEmailData(clientId) {
		let welcomeEmailData = super.createWelcomeEmailData(clientId || this.clientId);

		applyClientName(this.clientName, welcomeEmailData.data)
		applyCoachName(this.coachName, welcomeEmailData.data)

		return welcomeEmailData
	}

	createDripEmailData(clientId, dripReminderWrapper) {
		let dripEmailData = super.getReminderEmailData(clientId || this.clientId, dripReminderWrapper);

		applyClientName(this.clientName, dripEmailData.data)
		applyCoachName(this.coachName, dripEmailData.data)

		return dripEmailData
	}

	getLegalDocsRefs(clientId){
		return this.getParticipantWelcomeMaterials(clientId).filter(fileRef => fileRef.isLegalDoc())
	}


	static createFromCoachingPackageTemplate(coachingPackageTemplate){
		let purchasedPackage = plainToClass(PurchasedCoachingPackage, classToPlain(coachingPackageTemplate))

		purchasedPackage.regenerateId()
		purchasedPackage.setCreatedAtNow()

		purchasedPackage.parentTemplateId = coachingPackageTemplate.id
		purchasedPackage.netRevenue = null
		purchasedPackage.soldCount = null

		return purchasedPackage
	}

	getStatus(){
		return this.status
	}

	setStatus(status){
		this.status = status
	}

	setStatusArchived(){
		this.setStatus(StatusEnum.ARCHIVED)
	}
	setStatusActiveOrCompleted(){
		if(this.isCompletedByTime()){
			this.setStatus(StatusEnum.COMPLETED)
		} else {
			this.setStatus(StatusEnum.ACTIVE)
		}
	}
	setStatusCompleted(){
		this.setStatus(StatusEnum.COMPLETED)
	}
	isStatusCompleted(){
		return this.status === StatusEnum.COMPLETED
	}
	isStatusActive(){
		return this.status === StatusEnum.ACTIVE
	}
	isStatusArchived(){
		return this.status === StatusEnum.ARCHIVED
	}

	unarchive(){
		if(this.isStatusArchived){
			this.setStatusActiveOrCompleted()
		}
	}

	isCompletedByTime(){
		if(this.totalScheduledSessionsCount() <= this.totalSessionsCount()){
			return false
		}
		let ref = this.getLatestSessionRefByTime()
		if(!ref || ref.endTime() > timeNow()){
			return false
		}
		return true
	}

	hasParticipants(){
		return this.participantsCount() > 0
	}
	participantsCount(){
		return this.participantsList.length
	}

	getJoinedParticipants(){
		return this.participantsList.filter(participant => participant.isInviteAccepted())
	}

	joinedParticipantsCount(){
		return this.getJoinedParticipants().length
	}

	pendingParticipantsCount(){
		return this.participantsList.filter(participant => participant.isInvitePending()).length
	}
	maxParticipantsCount(){
		if(this.groupSettings){
			return this.groupSettings.participantsLimit
		}
		return 1
	}

	canInviteMoreParticipants(){
		return !this.isGroupFull()
	}

	isGroupFull(){
		if(this.groupSettings){
			return this.participantsList.length >= this.groupSettings.participantsLimit
		}
		return true
	}

	getInvoiceObjectType(){
		if(this.isGroupPackage()){
			return ObjectTypeEnum.COACHING_PACKAGE_GROUP
		}
		return ObjectTypeEnum.COACHING_PACKAGE
	}

	getPackageTypeFormatted(){
		switch (this.packageType){
			default:
			case CoachingPackageType.ONE_ON_ONE:
				return "One-on-one"
			case CoachingPackageType.GROUP:
				return `Group of ${this.maxParticipantsCount()} participants`
		}
	}

	participantsCountFormatted(){
		return `${this.participantsCount()}/${this.maxParticipantsCount()}`
	}

	createPackageRef(){
		return new CoachingPackageRef(this.id, this.title, this.status, this.packageType)
	}

	getParticipantWelcomeLegalDocs(participantId = this.clientId){
		return this.getParticipantWelcomeMaterials(participantId).filter(fileRef => fileRef.isLegalDoc())
	}

	getParticipantWelcomeMaterials(participantId){
		// welcomeMaterials.getLegalDocsFileRefs()
		if(!this.participantFileRefsMap[participantId]){
			this.participantFileRefsMap[participantId] = []
		}
		return this.participantFileRefsMap[participantId]
	}

	addParticipantWelcomeMaterials(participantId, fileRef){
		let fileRefsArray = this.getParticipantWelcomeMaterials(participantId)
		fileRefsArray.push(fileRef)
	}

	onResourceCompletedBy(clientId, resourceId){
		this.addClientResourceCompletion(clientId, resourceId)
	}

	getClientCompletionList(clientId){
		if(!this.completionMap[clientId]){
			this.completionMap[clientId] = []
		}
		return this.completionMap[clientId]
	}
	addClientResourceCompletion(clientId, resourceId){
		let clientCompletionArray = this.getClientCompletionList(clientId)
		let index = clientCompletionArray.findIndex(id => id === resourceId)
		if(index < 0){
			clientCompletionArray.push(resourceId)
		}
	}

	migrateSchema(oldVersion, newVersion) {
		super.migrateSchema(oldVersion, newVersion);
		console.log("PurchasedCoachingPackage.migrateSchema: oldVersion = ", oldVersion, " newVersion = ", newVersion)
		if(oldVersion < 2){
			this.invoicesInfo.migrateSchema()
			if(!this.invoicesInfo.amountTotal){
				this.invoicesInfo.amountTotal = this.totalPrice()
				this.invoicesInfo.amountPaid = this.totalPrice()
			}
		}
		if(oldVersion < 3){
			if(this.validTillMs && this.invoicesInfo){
				this.invoicesInfo.validTillMs = this.validTillMs
			}
		}
		if(oldVersion < 4){
			if(this.isOneOnOnePackage()){
				this.addParticipant(new ParticipantItem(this.clientId, this.clientName))
				let legalDocs = this.welcomeMaterials.getLegalDocsFileRefs()
				for(let legalDoc of legalDocs){
					this.addParticipantWelcomeMaterials(this.clientId, legalDoc)
				}
			}
		}
		if(oldVersion < 5){
			if(Array.isArray(this.participantIds)){
				this.sharedWithIds = this.participantIds
			}
		}
		if(oldVersion < 6){
			//this migration was performed in the backend under migrateTeamFeature migration
		}

	}

}
